/**
 * Investigations API client
 *
 * Route contract notes:
 * - Base ENDPOINT = '/investigations'
 * - Evidence packet endpoint: '/packet' -> final: '/api/investigations/{id}/packet'
 * - Backend route: POST "/{investigation_id}/packet" with prefix "/api/investigations"
 * - Contract verified: frontend and backend paths match correctly
 */
import apiClient, { type RequestConfig } from './client';
import type {
  Investigation,
  AgentFinding,
  TimelineEvent,
  InvestigationEntity,
  CreateInvestigationRequest,
  PaginatedResponse,
  PaginationParams,
} from './types';

const ENDPOINT = '/investigations';

export const investigationsApi = {
  /**
   * Create a new investigation.
   */
  async create(
    request: CreateInvestigationRequest,
    config?: RequestConfig
  ): Promise<Investigation> {
    const { data } = await apiClient.post<Investigation>(ENDPOINT, request, config);
    return data;
  },

  /**
   * Get an investigation by ID.
   */
  async get(id: string, config?: RequestConfig): Promise<Investigation> {
    const { data } = await apiClient.get<Investigation>(`${ENDPOINT}/${id}`, config);
    return data;
  },

  /**
   * List investigations for a matter.
   */
  async listForMatter(
    matterId: string,
    params?: PaginationParams & { status?: string },
    config?: RequestConfig
  ): Promise<PaginatedResponse<Investigation>> {
    const { data } = await apiClient.get<PaginatedResponse<Investigation>>(
      `${ENDPOINT}/matter/${matterId}`,
      {
        ...config,
        params: params as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Start running an investigation.
   */
  async start(id: string, config?: RequestConfig): Promise<{
    investigation_id: string;
    job_id: string;
    status: string;
    message: string;
  }> {
    const { data } = await apiClient.post<{
      investigation_id: string;
      job_id: string;
      status: string;
      message: string;
    }>(`${ENDPOINT}/${id}/run`, null, config);
    return data;
  },

  /**
   * Pause a running investigation.
   */
  async pause(id: string, config?: RequestConfig): Promise<Investigation> {
    const { data } = await apiClient.post<Investigation>(`${ENDPOINT}/${id}/pause`, null, config);
    return data;
  },

  /**
   * Resume a paused investigation.
   */
  async resume(id: string, config?: RequestConfig): Promise<{
    investigation_id: string;
    job_id: string;
    status: string;
  }> {
    const { data } = await apiClient.post<{
      investigation_id: string;
      job_id: string;
      status: string;
    }>(`${ENDPOINT}/${id}/resume`, null, config);
    return data;
  },

  /**
   * Cancel an investigation.
   */
  async cancel(id: string, config?: RequestConfig): Promise<Investigation> {
    const { data } = await apiClient.post<Investigation>(`${ENDPOINT}/${id}/cancel`, null, config);
    return data;
  },

  /**
   * Update investigation settings.
   */
  async update(
    id: string,
    updates: {
      name?: string;
      claims?: string[];
      entities?: string[];
      date_start?: string;
      date_end?: string;
      privilege_mode?: 'exclude' | 'flag' | 'include';
      budget_tokens?: number;
    },
    config?: RequestConfig
  ): Promise<Investigation> {
    const { data } = await apiClient.patch<Investigation>(`${ENDPOINT}/${id}`, updates, config);
    return data;
  },

  /**
   * Delete an investigation.
   */
  async delete(id: string, config?: RequestConfig): Promise<void> {
    await apiClient.delete(`${ENDPOINT}/${id}`, config);
  },

  /**
   * Get findings for an investigation.
   */
  async getFindings(
    id: string,
    params?: PaginationParams & {
      agent_type?: string;
      finding_type?: string;
      min_confidence?: number;
      verified_only?: boolean;
    },
    config?: RequestConfig
  ): Promise<PaginatedResponse<AgentFinding>> {
    const { data } = await apiClient.get<PaginatedResponse<AgentFinding>>(
      `${ENDPOINT}/${id}/findings`,
      {
        ...config,
        params: params as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Verify/reject a finding.
   */
  async verifyFinding(
    id: string,
    findingId: string,
    verified: boolean,
    notes?: string,
    config?: RequestConfig
  ): Promise<AgentFinding> {
    const { data } = await apiClient.post<AgentFinding>(
      `${ENDPOINT}/${id}/findings/${findingId}/verify`,
      { verified, notes },
      config
    );
    return data;
  },

  /**
   * Get timeline events for an investigation.
   */
  async getTimeline(
    id: string,
    params?: {
      significance?: 'low' | 'medium' | 'high' | 'critical';
      from_date?: string;
      to_date?: string;
    },
    config?: RequestConfig
  ): Promise<TimelineEvent[]> {
    const { data } = await apiClient.get<TimelineEvent[]>(
      `${ENDPOINT}/${id}/timeline`,
      {
        ...config,
        params: params as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Get entities for an investigation.
   */
  async getEntities(
    id: string,
    params?: {
      entity_type?: string;
      min_mentions?: number;
    },
    config?: RequestConfig
  ): Promise<InvestigationEntity[]> {
    const { data } = await apiClient.get<InvestigationEntity[]>(
      `${ENDPOINT}/${id}/entities`,
      {
        ...config,
        params: params as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Merge entities (for disambiguation).
   */
  async mergeEntities(
    id: string,
    sourceIds: string[],
    targetId: string,
    config?: RequestConfig
  ): Promise<InvestigationEntity> {
    const { data } = await apiClient.post<InvestigationEntity>(
      `${ENDPOINT}/${id}/entities/merge`,
      { source_ids: sourceIds, target_id: targetId },
      config
    );
    return data;
  },

  /**
   * Generate evidence packet from investigation.
   */
  async generateEvidencePacket(
    id: string,
    options?: {
      include_timeline?: boolean;
      include_entities?: boolean;
      include_source_docs?: boolean;
      format?: 'pdf' | 'docx' | 'json';
    },
    config?: RequestConfig
  ): Promise<{
    packet_id: string;
    job_id: string;
    status: string;
  }> {
    const { data } = await apiClient.post<{
      packet_id: string;
      job_id: string;
      status: string;
    }>(`${ENDPOINT}/${id}/packet`, options || {}, config);
    return data;
  },

  /**
   * Download the most recent evidence packet.
   */
  async downloadEvidencePacket(
    id: string,
    _packetId?: string,
    config?: RequestConfig
  ): Promise<Blob> {
    const { data } = await apiClient.get<Blob>(
      `${ENDPOINT}/${id}/packet/download`,
      config
    );
    return data;
  },

  /**
   * Get investigation usage/cost metrics.
   */
  async getUsage(id: string, config?: RequestConfig): Promise<{
    tokens_used: number;
    tokens_remaining: number;
    cost_estimate: number;
    agent_breakdown: Record<string, { tokens: number; findings: number }>;
    documents_analyzed: number;
    runtime_seconds: number;
  }> {
    const { data } = await apiClient.get<{
      tokens_used: number;
      tokens_remaining: number;
      cost_estimate: number;
      agent_breakdown: Record<string, { tokens: number; findings: number }>;
      documents_analyzed: number;
      runtime_seconds: number;
    }>(`${ENDPOINT}/${id}/usage`, config);
    return data;
  },

  /**
   * Subscribe to investigation updates via SSE.
   */
  subscribeToUpdates(
    id: string,
    handlers: {
      onFinding?: (finding: AgentFinding) => void;
      onProgress?: (progress: { percent: number; message: string }) => void;
      onComplete?: (investigation: Investigation) => void;
      onError?: (error: Event) => void;
    }
  ): EventSource {
    const eventSource = apiClient.sse(`${ENDPOINT}/${id}/stream`, {
      onMessage: (event) => {
        const data = JSON.parse(event.data);
        switch (data.type) {
          case 'finding':
            handlers.onFinding?.(data.payload);
            break;
          case 'progress':
            handlers.onProgress?.(data.payload);
            break;
          case 'complete':
            handlers.onComplete?.(data.payload);
            break;
        }
      },
      onError: handlers.onError,
    });
    return eventSource;
  },

  /**
   * Get investigation knowledge graph (nodes & links).
   */
  async getGraph(
    id: string,
    minRelevance: number = 0.5,
    config?: RequestConfig
  ): Promise<any> {
    const { data } = await apiClient.get<any>(
      `${ENDPOINT}/${id}/graph`,
      { ...config, params: { min_relevance: minRelevance } }
    );
    return data;
  },

  /**
   * Get matter-wide temporal knowledge graph.
   */
  async getTemporalGraph(
    matterId: string,
    params?: { start_date?: string; end_date?: string },
    config?: RequestConfig
  ): Promise<any> {
    const { data } = await apiClient.get<any>(
      `${ENDPOINT}/matter/${matterId}/graph/temporal`,
      { ...config, params }
    );
    return data;
  },

  /**
   * Get matter-wide timeline events for the evidence graph.
   */
  async getTimelineEvents(
    matterId: string,
    params?: { start_date?: string; end_date?: string },
    config?: RequestConfig
  ): Promise<TimelineEventsResponse> {
    const { data } = await apiClient.get<TimelineEventsResponse>(
      `${ENDPOINT}/matter/${matterId}/graph/timeline-events`,
      { ...config, params }
    );
    return data;
  },

  /**
   * Get narrative arcs for an investigation.
   */
  async getNarrativeArcs(
    investigationId: string,
    config?: RequestConfig
  ): Promise<NarrativeArcsResponse> {
    const { data } = await apiClient.get<NarrativeArcsResponse>(
      `${ENDPOINT}/${investigationId}/narrative-arcs`,
      config
    );
    return data;
  },

  /**
   * Extract and normalize entities for an investigation.
   */
  async extractEntities(
    investigationId: string,
    maxDocuments?: number,
    config?: RequestConfig
  ): Promise<{ entities: number; relationships: number; documents_processed: number; raw_extractions: number }> {
    const params = maxDocuments ? { max_documents: maxDocuments } : undefined;
    const { data } = await apiClient.post<{ entities: number; relationships: number; documents_processed: number; raw_extractions: number }>(
      `${ENDPOINT}/${investigationId}/entities/extract`,
      null,
      { ...config, params }
    );
    return data;
  },

  /**
   * Split an entity alias into a new entity.
   */
  async splitEntity(
    investigationId: string,
    entityId: string,
    alias: string,
    config?: RequestConfig
  ): Promise<{ id: string; canonical_name: string; entity_type: string }> {
    const { data } = await apiClient.post<{ id: string; canonical_name: string; entity_type: string }>(
      `${ENDPOINT}/${investigationId}/entities/split`,
      null,
      { ...config, params: { entity_id: entityId, alias } }
    );
    return data;
  },
};

export interface TimelineEventItem {
  id: string;
  event_date: string | null;
  description: string;
  document_id: string;
  document_filename: string;
  excerpt: string;
  date_confidence: number | null;
  extraction_method: string | null;
}

export interface TimelineEventsResponse {
  events: TimelineEventItem[];
  date_range: { min: string; max: string } | null;
}

// Narrative Arc types
export interface NarrativePhaseEvent {
  id: string;
  date: string;
  description: string;
  document_id: string;
  confidence: number | null;
  excerpt: string;
}

export interface NarrativePhase {
  label: string;
  summary: string;
  is_turning_point: boolean;
  event_count: number;
  events: NarrativePhaseEvent[];
}

export interface NarrativeArc {
  arc_id: string;
  title: string;
  summary: string;
  phases: NarrativePhase[];
  event_ids: string[];
  entity_names: string[];
  confidence: number;
  date_range: { start: string | null; end: string | null };
}

export interface NarrativeArcsResponse {
  investigation_id: string;
  arcs: NarrativeArc[];
  total_arcs: number;
}

export default investigationsApi;
